import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, API_BASE } from "@/lib/api";

const DEFAULT_BANNERS = [
  { id: "d1", url: "https://images.unsplash.com/photo-1693031630369-bd429a57f115?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", caption: "Same-day flex banners across Bengaluru" },
  { id: "d2", url: "https://images.unsplash.com/photo-1571217748526-261477b6ac62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", caption: "Glowing LED name boards & signage" },
  { id: "d3", url: "https://images.unsplash.com/photo-1693031630146-568e2f72db0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", caption: "Premium vinyl & storefront branding" },
];

const LINK_LABELS = { quote: "Get Quote", services: "View Services", pricing: "See Pricing", area: "Service Area" };

export const Banner3D = () => {
  const [banners, setBanners] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touchX = useRef(null);

  useEffect(() => {
    api.get("/banners")
      .then((r) => {
        const items = (r.data || []).map((b) => ({ id: b.id, url: `${API_BASE}/banners/${b.id}/image`, caption: b.caption }));
        setBanners(items.length ? items : DEFAULT_BANNERS);
      })
      .catch(() => setBanners(DEFAULT_BANNERS));
  }, []);

  const slides = banners || DEFAULT_BANNERS;

  const go = useCallback((dir) => setIndex((i) => (i + dir + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    timer.current = setInterval(() => setIndex((i) => (i + 1) % slides.length), 4200);
    return () => clearInterval(timer.current);
  }, [slides.length, paused]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  if (!slides.length) return null;
  const current = slides[index];

  return (
    <section data-testid="home-3d-banner" className="bg-white py-8 lg:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-xl"
          style={{ perspective: "1400px" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="relative h-56 sm:h-72 lg:h-[22rem] bg-slate-900">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id + index}
                initial={{ rotateY: 75, opacity: 0, scale: 0.94 }}
                animate={{ rotateY: 0, opacity: 1, scale: 1 }}
                exit={{ rotateY: -75, opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
                style={{ transformStyle: "preserve-3d" }}
              >
                <img src={current.url} alt={current.caption || "banner"} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
                  <span className="inline-block rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Creative Prints</span>
                  {current.caption && (
                    <h3 className="mt-2 max-w-2xl font-heading text-xl sm:text-2xl lg:text-3xl font-extrabold text-white leading-tight">{current.caption}</h3>
                  )}
                  <a href={`#${current.link || "quote"}`} data-testid="banner-get-quote" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-amber-700 transition-colors">
                    {LINK_LABELS[current.link] || "Get Quote"}
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {slides.length > 1 && (
            <>
              <button data-testid="banner-prev" onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/85 text-slate-800 shadow hover:bg-white transition-colors">
                <ChevronLeft size={20} />
              </button>
              <button data-testid="banner-next" onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/85 text-slate-800 shadow hover:bg-white transition-colors">
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-4 right-5 flex gap-1.5">
                {slides.map((_, i) => (
                  <button key={i} data-testid={`banner-dot-${i}`} onClick={() => setIndex(i)} className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-primary" : "w-2 bg-white/60"}`} aria-label={`Slide ${i + 1}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
